import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "../../styles/Dropdown.css";

interface DropdownProps {
  open: boolean;
  anchorRef: React.RefObject<any>;
  children: React.ReactNode;
  className?: string;
  onClose?: () => void;
}

function Dropdown({
  open,
  anchorRef,
  children,
  className,
  onClose,
}: DropdownProps) {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ top: number; left: number }>({
    top: 0,
    left: 0,
  });

  useEffect(() => {
    function updatePosition() {
      if (anchorRef?.current && open) {
        const rect = anchorRef.current.getBoundingClientRect();
        setPosition({
          top: rect.bottom + window.scrollY - 15,
          left: rect.left + window.scrollX + 28,
        });
      }
    }

    updatePosition();

    if (open) {
      window.addEventListener("scroll", updatePosition, true);
      window.addEventListener("resize", updatePosition);
    }

    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [anchorRef, open]);

  useEffect(() => {
    if (!open) return;
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;

      if (dropdownRef.current?.contains(target)) {
        return;
      }

      if (anchorRef?.current?.contains(target)) {
        return;
      }

      onClose && onClose();
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open, onClose, anchorRef]);

  if (!open) return null;

  return createPortal(
    <div
      ref={dropdownRef}
      className={className}
      style={{
        position: "absolute",
        top: position.top,
        left: position.left,
        zIndex: 9999,
        minWidth: 80,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {children}
    </div>,
    document.body
  );
}

export default Dropdown;
